import { AssociatedContact } from '@app/models/AssociatedContact'
import { Company } from '@app/models/Company'
import { Contact, IContact } from '@app/models/Contact'
import { IEntityAllow } from '@app/models/EntityAllow'
import { IMembership } from '@app/models/Membership'
import { IUser, User } from '@app/models/User'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IUserEditState {
  readonly canSave: boolean
  readonly user: IContact & IUser
}

const StateRecord = Record({
  canSave: false,
  user: undefined,
})

export class UserEditState extends StateRecord implements IUserEditState {
  public static fromJS(initialState?: Partial<IUserEditState>) {
    return new UserEditState(initialState)
  }

  public readonly canSave: boolean
  public readonly user: Contact & User

  constructor(props?: Partial<IUserEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  ChangeAccess = 'USER_EDIT::CHANGE_ACCESS',
  ChangeMembership = 'USER_EDIT::CHANGE_MEMBERSHIP',
  LoadUser = 'USER_EDIT::LOAD_USER',
  LoadUserSuccess = 'USER_EDIT::LOAD_USER_SUCCESS',
  ResetState = 'USER_EDIT::RESET_STATE',
  SaveUser = 'USER_EDIT::SAVE_USER',
  SaveUserFailure = 'USER_EDIT::SAVE_USER_FAILURE',
  SaveUserSuccess = 'USER_EDIT::SAVE_USER_SUCCESS',
  SetCompany = 'USER_EDIT::SET_COMPANY',
  SetConfirmPassword = 'USER_EDIT::SET_CONFIRM_PASSWORD',
  SetEmail = 'USER_EDIT::SET_EMAIL',
  SetFirstName = 'USER_EDIT::SET_FIRST_NAME',
  SetLastName = 'USER_EDIT::SET_LAST_NAME',
  SetPassword = 'USER_EDIT::SET_PASSWORD',
  SetPermission = 'USER_EDIT::SET_PERMISSION',
  SetStatus = 'USER_EDIT::SET_STATUS',
  SetTitle = 'USER_EDIT::SET_TITLE',
}

interface IChangeAccessAction {
  type: ActionTypes.ChangeAccess
  value: List<IEntityAllow>
}

interface IChangeMembershipAction {
  type: ActionTypes.ChangeMembership
  value: List<IMembership>
}

interface ILoadUserAction {
  type: ActionTypes.LoadUser
  value?: string
}

interface ILoadUserSuccessAction {
  type: ActionTypes.LoadUserSuccess
  value: Contact
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveUserAction {
  type: ActionTypes.SaveUser
  value?: string
}

interface ISaveUserFailureAction {
  type: ActionTypes.SaveUserFailure
  error: string
}

interface ISaveUserSuccessAction {
  type: ActionTypes.SaveUserSuccess
}

interface ISetCompanyAction {
  type: ActionTypes.SetCompany
  value?: Company
}

interface ISetConfirmPasswordAction {
  type: ActionTypes.SetConfirmPassword
  value: string
}

interface ISetEmailAction {
  type: ActionTypes.SetEmail
  value: string
}

interface ISetFirstNameAction {
  type: ActionTypes.SetFirstName
  value: string
}

interface ISetLastNameAction {
  type: ActionTypes.SetLastName
  value: string
}

interface ISetPasswordAction {
  type: ActionTypes.SetPassword
  value: string
}

interface ISetPermissionAction {
  permission: string
  type: ActionTypes.SetPermission
  value: boolean
}

interface ISetStatusAction {
  type: ActionTypes.SetStatus
  value: string
}

interface ISetTitleAction {
  type: ActionTypes.SetTitle
  value: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IChangeAccessAction
  | IChangeMembershipAction
  | ILoadUserAction
  | ILoadUserSuccessAction
  | IResetStateAction
  | ISaveUserAction
  | ISaveUserFailureAction
  | ISaveUserSuccessAction
  | ISetCompanyAction
  | ISetConfirmPasswordAction
  | ISetEmailAction
  | ISetFirstNameAction
  | ISetLastNameAction
  | ISetPasswordAction
  | ISetPermissionAction
  | ISetStatusAction
  | ISetTitleAction

// -------------------------
// ACTION CREATORS
// -------------------------

function changeAccess(value: List<IEntityAllow>): IChangeAccessAction {
  return {
    type: ActionTypes.ChangeAccess,
    value,
  }
}

function changeMembership(value: List<IMembership>): IChangeMembershipAction {
  return {
    type: ActionTypes.ChangeMembership,
    value,
  }
}

function loadUser(value?: string): ILoadUserAction {
  return { type: ActionTypes.LoadUser, value }
}

function loadUserSuccess(value: Contact): ILoadUserSuccessAction {
  return {
    type: ActionTypes.LoadUserSuccess,
    value,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function save(value?: string): ISaveUserAction {
  return {
    type: ActionTypes.SaveUser,
    value,
  }
}

function saveFailure(error: string): ISaveUserFailureAction {
  return {
    error,
    type: ActionTypes.SaveUserFailure,
  }
}

function saveSuccess(): ISaveUserSuccessAction {
  return {
    type: ActionTypes.SaveUserSuccess,
  }
}

function setCompany(value?: Company): ISetCompanyAction {
  return {
    type: ActionTypes.SetCompany,
    value,
  }
}

function setConfirmPassword(value: string): ISetConfirmPasswordAction {
  return {
    type: ActionTypes.SetConfirmPassword,
    value,
  }
}

function setEmail(value: string): ISetEmailAction {
  return {
    type: ActionTypes.SetEmail,
    value,
  }
}

function setFirstName(value: string): ISetFirstNameAction {
  return {
    type: ActionTypes.SetFirstName,
    value,
  }
}

function setLastName(value: string): ISetLastNameAction {
  return {
    type: ActionTypes.SetLastName,
    value,
  }
}

function setPassword(value: string): ISetPasswordAction {
  return {
    type: ActionTypes.SetPassword,
    value,
  }
}

function setPermission(
  value: boolean,
  permission: string,
): ISetPermissionAction {
  return {
    permission,
    type: ActionTypes.SetPermission,
    value,
  }
}

function setStatus(value: string): ISetStatusAction {
  return {
    type: ActionTypes.SetStatus,
    value,
  }
}

function setTitle(value: string): ISetTitleAction {
  return { type: ActionTypes.SetTitle, value }
}

export const actionCreators = {
  changeAccess,
  changeMembership,
  loadUser,
  loadUserSuccess,
  resetState,
  save,
  setCompany,
  setConfirmPassword,
  setEmail,
  setFirstName,
  setLastName,
  setPassword,
  setPermission,
  setStatus,
  setTitle,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<UserEditState> = (
  state: UserEditState = new UserEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.ChangeAccess:
      return state.setIn(
        ['user', 'user', 'allowedAccessTo'],
        action.value,
      ) as UserEditState

    case ActionTypes.ChangeMembership:
      return state.setIn(
        ['user', 'user', 'memberOf'],
        action.value,
      ) as UserEditState

    case ActionTypes.LoadUser:
      if (!action.value) {
        const newContact = new Contact()
        return state.set('user', newContact) as UserEditState
      } else {
        return state as UserEditState
      }

    case ActionTypes.LoadUserSuccess:
      return state
        .set('user', action.value)
        .set('canSave', true) as UserEditState

    case ActionTypes.ResetState:
      return state.set('canSave', false).set('user', undefined) as UserEditState

    case ActionTypes.SaveUser:
      return state as UserEditState

    case ActionTypes.SaveUserFailure:
      return state as UserEditState

    case ActionTypes.SaveUserSuccess:
      return state as UserEditState

    case ActionTypes.SetCompany:
      return state.setIn(
        ['user', 'company'],
        AssociatedContact.fromJS(action.value),
      ) as UserEditState

    case ActionTypes.SetConfirmPassword:
      return state.setIn(
        ['user', 'user', 'confirmPassword'],
        action.value,
      ) as UserEditState

    case ActionTypes.SetEmail:
      return state.setIn(
        ['user', 'user', 'username'],
        action.value,
      ) as UserEditState

    case ActionTypes.SetFirstName:
      return state
        .setIn(['user', 'firstName'], action.value)
        .set(
          'canSave',
          !state.user.isCompany &&
            action.value.length > 0 &&
            state.user.lastName.length > 0,
        ) as UserEditState

    case ActionTypes.SetLastName:
      return state
        .setIn(['user', 'lastName'], action.value)
        .set(
          'canSave',
          state.user.isCompany
            ? action.value.length > 0
            : action.value.length > 0 && state.user.firstName.length > 0,
        ) as UserEditState

    case ActionTypes.SetPassword:
      return state.setIn(
        ['user', 'user', 'password'],
        action.value,
      ) as UserEditState

    case ActionTypes.SetPermission:
      const perm = state.user.user.permissions.specialPermissions
      perm[action.permission] = action.value

      return state
        .setIn(['user', 'user', 'permissions', 'specialPermissions'], perm)
        .set('canSave', !state.canSave) as UserEditState

    case ActionTypes.SetStatus:
      return state.setIn(
        ['user', 'user', 'status'],
        action.value,
      ) as UserEditState

    case ActionTypes.SetTitle:
      return state.setIn(['user', 'title'], action.value) as UserEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadUserSaga(action: ILoadUserAction) {
  try {
    if (action.value) {
      const baseUrl = `/api/Users/${action.value}`

      const result = yield makeAuthenticatedRequest(baseUrl)

      if (result.success === true) {
        yield put(resetState())
        yield put(loadUserSuccess(Contact.fromJS(result.entity)))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    } else {
      yield put(loadUserSuccess(new Contact({ user: new User() })))
    }
  } catch (err) {
    console.error(err)
  }
}

function* saveUserSaga(action: ISaveUserAction) {
  try {
    const state = yield select(selectModuleState())

    const baseUrl = action.value ? `/api/Users/${action.value}` : '/api/Users'

    const user = state.user.toJS()

    for (const p of Object.keys(user.user.permissions.specialPermissions)) {
      user.user.permissions[p] = user.user.permissions.specialPermissions[p]
    }

    const userOpts = {
      body: JSON.stringify(user),
      method: action.value ? RequestMethod.PUT : RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, userOpts)

    if (result.success === true) {
      yield put(saveSuccess())
      yield put(resetState())
      yield put(
        successToast(
          'Success',
          action.value
            ? `${state.user.fullName}'s information updated`
            : 'New User Created',
        ),
      )
      yield put(push(`/admin/user/${action.value || result.entity.id}`))
    } else {
      yield put(saveFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadUser() {
  yield takeLatest(ActionTypes.LoadUser, loadUserSaga)
}

function* watchSaveUser() {
  yield takeLatest(ActionTypes.SaveUser, saveUserSaga)
}

export function* userEditRootSaga() {
  yield fork(watchLoadUser)
  yield fork(watchSaveUser)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): UserEditState =>
  state.userEdit

export const selectCanSave = () =>
  createSelector(selectModuleState(), (mState) => mState.canSave)

export const selectUser = () =>
  createSelector(selectModuleState(), (mState) => mState.user)
